<template>
  <div
    class="game columns is-variable is-0"
  >
    <category
      class="game__categories"
      v-for="(category, key, index) in currentCategories"
      :category="category"
      :name="key"
      :key="key"
      :highlighted="index === highlightedCategoryIndex"
      :class="{ 'is-highlighted': index === highlightedCategoryIndex }"
    ></category>

    <active-question
      v-if="activeQuestion"
      :question="activeQuestion"
    ></active-question>

    <float-menu
      :display="formattedAirDate"
      :position="160"
      class="is-hidden-mobile"
    ></float-menu>

    <float-menu
      :display="'$' + scoring.totalCoryat"
      :position="80"
    ></float-menu>

    <float-menu
      :position="40"
      event="load-next-round"
    ></float-menu>
  </div>
</template>

<script>
  // import game from '@/data/sample-game.json';
  import emptyScoring from '@/emptyscoring';
  import EventBus from '@/EventBus';
  import FloatMenu from '@/components/FloatMenu.vue';
  import Category from './Category.vue';
  import ActiveQuestion from './ActiveQuestion.vue';


  export default {
    name: 'Game',

    props: {
      id: Number,
      game: Object,
      mode: String,
    },

    components: {
      Category,
      FloatMenu,
      ActiveQuestion,
    },

    data() {
      return {
        round: 1,
        coryat: 0,
        correctQuestions: [],
        incorrectQuestions: [],
        scoring: null,
        highlightedCategoryIndex: 0,
        activeQuestion: null,
      };
    },

    created() {
      EventBus.$on('answered', this.hideQuestion);
      EventBus.$on('passed', this.hideQuestion);
      EventBus.$on('revealed', this.showQuestion);

      EventBus.$on('answered', this.scoreAnswer);
      EventBus.$on('revealed', this.scoreReveal);
      EventBus.$on('passed', this.scorePass);

      EventBus.$on('new-game', this.loadNewGame);
      EventBus.$on('load-next-round', this.loadNextRound);

      window.addEventListener('keyup', this.decHighlightedCategory);
      window.addEventListener('keyup', this.incHighlightedCategory);


      this.loadNewGame();
    },

    destroyed() {
      window.removeEventListener('keyup', this.decHighlightedCategory);
      window.removeEventListener('keyup', this.incHighlightedCategory);
    },

    methods: {
      loadNewGame() {
        this.coryat = 0;
        this.correctQuestions = [];
        this.incorrectQuestions = [];
        this.passedQuestions = [];
        this.round = 1;
        this.highlightedCategoryIndex = 0;

        if (this.scoring) {
          if (this.scoring.totalCorrect > 0 || this.scoring.totalIncorrect > 0) {
            // this way we'll only track games played if they've answered any questions
            this.scoring.gamesPlayed = 1;
          }

          this.$emit('update-scoring', this.scoring);
        }

        this.scoring = Object.assign({}, emptyScoring);
        this.scoring.totalLiveCoryat = this.game.coryat;

        Object.values(this.game.questions).forEach((q) => {
          this.scoring.totalPossibleCoryat += parseInt(q.value, 10);
        });
      },

      loadNextRound() {
        if (this.round > 2) {
          EventBus.$emit('load-next-game');
          return;
        }

        this.round += 1;
      },

      loadNextQuestion() {
        // find which round we're on, then select random question which is unanswered
        let activeQuestions = this.game.questions.filter((obj) => {
          // questions with state will always have been answered already
          return (!obj.state) && obj.game_round === this.round;
        });

        if (!activeQuestions || activeQuestions.length === 0) {
          this.hideQuestion();
          return;
        }

        let q = activeQuestions[Math.floor(Math.random() * activeQuestions.length)];

        this.showQuestion(q);
      },

      scoreAnswer(correct, question) {
        if (correct) {
          if (parseInt(question.value, 10) === 0) {
            // final jeopardy
            this.scoring.fjCorrect += 1;
          } else {
            this.scoring.totalCoryat += parseInt(question.value, 10);
            this.scoring.totalCorrect += 1; // does not include FJs
          }
          this.correctQuestions.push(question);
        } else {
          if (parseInt(question.value, 10) === 0) {
            // final jeopardy
            this.scoring.fjIncorrect += 1;
          } else {
            this.scoring.totalCoryat -= parseInt(question.value, 10);
            this.scoring.totalIncorrect += 1; // does not include FJs
          }
          this.incorrectQuestions.push(question);
        }

        if (this.mode === 'rapidfire') {
          this.loadNextQuestion();
        }
      },

      showQuestion(question) {
        this.activeQuestion = question;
      },

      hideQuestion() {
        this.activeQuestion = null;
      },

      scoreReveal(question) {
        if (parseInt(question.value, 10) === 0) {
          // final jeopardy
          this.scoring.fjRevealed += 1;
          return;
        }

        // not including FJ
        this.scoring.totalRevealed += 1;
        this.scoring.totalRevealedCoryat += parseInt(question.value, 10);
      },

      scorePass(question) {
        if (parseInt(question.value, 10) === 0) {
          // finalJeopardy
          this.scoring.fjPassed += 1;
          return;
        }

        this.scoring.totalPassed += 1;
        this.scoring.totalPassedCoryat += parseInt(question.value, 10);
        this.passedQuestions.push(question);
      },

      incHighlightedCategory(evt) {
        if (evt.key !== 'ArrowRight') return;

        this.highlightedCategoryIndex += 1;
        if (this.highlightedCategoryIndex > Object.keys(this.currentCategories).length - 1) {
          this.highlightedCategoryIndex = 0;
        }
      },

      decHighlightedCategory(evt) {
        if (evt.key !== 'ArrowLeft') return;

        this.highlightedCategoryIndex -= 1;
        if (this.highlightedCategoryIndex < 0) {
          this.highlightedCategoryIndex = Object.keys(this.currentCategories).length - 1;
        }
      },

    },

    computed: {
      // we're not actually using cats from API yet (we need questions for them anyway)
      categories() {
        const cats = {};
        this.game.questions.forEach((obj) => {
          if (!(obj.game_round in cats)) cats[obj.game_round] = {};

          if (obj.category.category in cats[obj.game_round]) {
            cats[obj.game_round][obj.category.category].push(obj);
          } else {
            cats[obj.game_round][obj.category.category] = [obj];
          }
        });

        return cats;
      },

      currentCategories() {
        return this.categories[this.round];
      },

      formattedAirDate() {
        const options = {
          weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC',
        };

        return new Date(this.game.pretty_air_date.split()).toLocaleDateString('en-US', options);
      },
    },
  };
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
  $blue: rgb(6, 12, 234);

  .game {
    background-color: $blue;
    color: #FFFFFF;
    margin-top: 36px;
  }

  @media print, screen and (min-width:769px) {
    .game {
      margin-top: 0;
    }
  }

</style>
